import { generatePath } from 'react-router-dom';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { journalTexts } from 'consts/text';
import { appPaths } from 'consts/paths/paths';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { AutomaticCheckJournalAddForm } from 'components/templates/automaticCheckJournal/AutomaticCheckJournalAddForm/AutomaticCheckJournalAddForm';
import { AutomaticCheckJournalFormFields } from 'types';
import { parseAutomaticCheckJournalFormFieldsIntoAddAutomaticCheckJournalDTO } from 'utils/parsers/automaticCheckJournal/parseAutomaticCheckJournalFormFieldsDTOIntoAddAutomaticCheckJournalDTO';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { JournalPagePropsWithJournalType } from 'pages/app/staff/journal/journalPagePropsWithJournalType';

export const AutomaticCheckJournalAddPage = ({ journalType }: JournalPagePropsWithJournalType) => {
    const { journalId } = useJournalId();
    const { displaySuccess } = useSimpleNotification();
    const { statefulNavigate } = useNavigateWithState();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType,
    });

    const handleAddAutomaticCheckJournalSuccess = () => {
        displaySuccess(
            journalTexts.journalDetails.common.automaticCheckJournal
                .successfullyAddedAutomaticCheck,
        );

        const targetPath =
            journalType === 'SPECIAL'
                ? appPaths.app.journals.specialJournalDetails.pickedJournal.automaticCheckJournal
                      .base
                : appPaths.app.journals.groupJournalDetails.pickedJournal.automaticCheckJournal
                      .base;

        statefulNavigate(generatePath(targetPath, { journalId }), { replace: true });
    };

    const {
        mutate: addAutomaticCheckJournal,
        error: addAutomaticCheckJournalError,
        isLoading: isAddAutomaticCheckJournalLoading,
    } = useAppMutation(StaffHTTPService.automaticCheckJournal.addAutomaticCheckJournal, {
        key: ['ADD_AUTOMATIC_CHECK_JOURNAL'],
        onSuccess: handleAddAutomaticCheckJournalSuccess,
        invalidateQueryKeys: [['AUTOMATIC_CHECK_JOURNALS', journalId]],
    });

    const handleAddAutomaticCheckJournalFinish = (
        automaticCheckJournalFormData: AutomaticCheckJournalFormFields,
    ) => {
        const parsedAddAutomaticCheckJournalDTO =
            parseAutomaticCheckJournalFormFieldsIntoAddAutomaticCheckJournalDTO(
                automaticCheckJournalFormData,
            );
        addAutomaticCheckJournal({
            journalId,
            addAutomaticCheckJournalDTO: parsedAddAutomaticCheckJournalDTO,
        });
    };

    return (
        <AutomaticCheckJournalAddForm
            maxWidth={695}
            name="addAutomaticCheckJournal"
            axiosError={addAutomaticCheckJournalError}
            isLoading={isAddAutomaticCheckJournalLoading}
            onFinish={handleAddAutomaticCheckJournalFinish}
        />
    );
};
